/** The worksheet definitions, served by the backend from
 * shared/definitions.json — pick lists, frame beats, text field hints.
 * User-facing text may be localized ({ nl, en }); resolve it with
 * pick() from lib/i18n. promptText is backend-only (English, for the
 * Runway prompts) and never shown. */

import { request } from "./client";
import type { LocalizedText } from "../lib/i18n";

export interface PickOption {
  value: string;
  label: LocalizedText;
  promptText?: string;
}

export interface PickList {
  key: string;
  label: LocalizedText;
  sheetLabel: string;
  scope: "frame" | "group";
  options: PickOption[];
}

export interface FrameDefinition {
  position: number;
  name: LocalizedText;
  subtitle: LocalizedText;
  explanation: LocalizedText;
  question: LocalizedText;
  distanceHint: LocalizedText;
}

export interface WhoInput {
  key: "whoRole" | "whoWearing" | "whoBody";
  hint: LocalizedText;
}

export interface Definitions {
  pickLists: PickList[];
  frames: FrameDefinition[];
  textFields: {
    who: { label: LocalizedText; inputs: WhoInput[] };
    whatHappens: { label: LocalizedText; hint: LocalizedText };
    whereAreWe: { label: LocalizedText; hint: LocalizedText };
  };
  sketchHint: LocalizedText;
}

/** Shown only for the moment between first paint and the definitions
 * fetch resolving; the served frames array is the real count. */
const DEFAULT_FRAME_COUNT = 5;

let cached: Definitions | null = null;

export async function fetchDefinitions(): Promise<Definitions> {
  if (!cached) {
    cached = await request<Definitions>("GET", "/api/v1/definitions/");
  }
  return cached;
}

/** How many frames a storyboard has — the length of the definitions'
 * frames array, the single source of truth either repo ever consults. */
export function frameCount(definitions: Definitions | null): number {
  return definitions?.frames.length ?? DEFAULT_FRAME_COUNT;
}

export function pickList(
  definitions: Definitions,
  key: string,
): PickList | undefined {
  return definitions.pickLists.find((p) => p.key === key);
}

export function frameDefinition(
  definitions: Definitions,
  position: number,
): FrameDefinition | undefined {
  return definitions.frames.find((f) => f.position === position);
}
